import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getApiBaseUrl } from '@/lib/api-base-url';
import { authFetch } from '@/lib/auth-fetch';
import type {
  CashFlowDirection,
  CashLedgerResponse,
  CashTransactionRow,
} from '@/features/finance/cash-ledger.types';
import type { MatchResult } from '@/features/finance/cash-reconciliation.types';

import { allocatedDownpaymentKey } from './use-allocated-downpayment';
import { blOrderInvoicesKey } from './use-bl-order-invoices';
import { invoicePaymentsKey } from './use-invoice-payments';
import type { InvoiceRow } from '../types';

export class LinkCashEventError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'LinkCashEventError';
    this.status = status;
  }
}

async function readError(response: Response): Promise<LinkCashEventError> {
  let message = `Request failed (${String(response.status)})`;
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string') message = body.message;
  } catch {
    // body not JSON
  }
  return new LinkCashEventError(response.status, message);
}

export function expectedDirection(invoice: InvoiceRow): CashFlowDirection | null {
  const direction = invoice.invoice_direction?.toLowerCase();
  if (direction === 'sale') return 'in';
  if (direction === 'purchase') return 'out';
  return null;
}

// Live unmatched cash transactions filtered to this invoice's currency and
// direction. Pulls the existing /finance/cash-transactions ledger and filters
// client-side; for the listing volumes we ship that's well under the cost of
// a dedicated backend endpoint.
export function useUnmatchedCashTransactionsForInvoice(invoice: InvoiceRow, enabled: boolean) {
  const direction = expectedDirection(invoice);
  return useQuery<CashTransactionRow[]>({
    queryKey: ['finance', 'cash-transactions', 'unmatched-for-invoice', invoice.id],
    enabled: enabled && direction !== null && invoice.currency !== null,
    queryFn: async () => {
      const params = new URLSearchParams({ currency: invoice.currency ?? '' });
      const response = await authFetch(
        `${getApiBaseUrl()}/finance/cash-transactions?${params.toString()}`,
        { method: 'GET' },
      );
      if (!response.ok) throw await readError(response);
      const body = (await response.json()) as CashLedgerResponse;
      return body.transactions.filter(
        (t) =>
          t.reconciliationStatus !== 'matched' &&
          t.currency === invoice.currency &&
          t.direction === direction,
      );
    },
  });
}

export interface LinkArgs {
  txnId: string;
  invoiceId: string;
  amount: string;
  force?: boolean;
}

export async function linkCashEventToInvoice({
  txnId,
  invoiceId,
  amount,
  force,
}: LinkArgs): Promise<MatchResult> {
  const response = await authFetch(
    `${getApiBaseUrl()}/finance/cash-transactions/${encodeURIComponent(txnId)}/match`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ allocations: [{ invoiceId, amount }], force: force ?? false }),
    },
  );
  if (!response.ok) throw await readError(response);
  return (await response.json()) as MatchResult;
}

export function useLinkCashEventToInvoice(invoice: InvoiceRow) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: linkCashEventToInvoice,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['finance', 'cash-transactions'] });
      void qc.invalidateQueries({ queryKey: invoicePaymentsKey(invoice.id) });
      if (invoice.order_id !== null) {
        void qc.invalidateQueries({ queryKey: ['order-advance-invoices', invoice.order_id] });
        void qc.invalidateQueries({ queryKey: ['order-invoices', invoice.order_id] });
      }
      if (invoice.bl_order_id !== null) {
        void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(invoice.bl_order_id) });
        void qc.invalidateQueries({ queryKey: allocatedDownpaymentKey(invoice.bl_order_id) });
      }
      void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
    },
  });
}
